import React, { useState } from 'react';
import { X, Download, Eye, FileText, Image, File } from 'lucide-react';
import { api } from '../utils/api';
import DocumentViewerModal from './DocumentViewerModal';

const VehicleDocumentViewer = ({ isOpen, onClose, vehicle }) => {
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);

  const documents = [
    { name: 'Road Tax Document', key: 'roadTaxDoc', icon: FileText },
    { name: 'Permit Document', key: 'permitDoc', icon: FileText },
    { name: 'Fitness Certificate', key: 'fitnessDoc', icon: FileText },
    { name: 'PUC Certificate', key: 'pucDoc', icon: FileText },
    { name: 'Insurance Document', key: 'insurance', icon: FileText },
    { name: 'Vehicle RC Document', key: 'vehicleRcDoc', icon: FileText },
    { name: 'Vehicle Photo', key: 'vehiclePhoto', icon: Image }
  ];

  const getDocumentUrl = async (documentKey) => {
    if (!vehicle?.documents?.[documentKey]) return null;
    
    try {
      // Use the new presigned URL endpoint
      const response = await api.get(`/vehicles/${vehicle._id}/document/${documentKey}`);
      const { presignedUrl } = response.data.data;
      return presignedUrl;
    } catch (error) {
      console.error('Error getting presigned URL:', error);
      
      // Fallback to original document URL if presigned generation fails
      const documentPath = vehicle.documents[documentKey];
      if (documentPath.startsWith('http')) {
        return documentPath;
      }
      
      throw error;
    }
  };

  const handleDocumentClick = async (document) => {
    setIsLoadingDocument(true);
    try {
      const url = await getDocumentUrl(document.key);
      if (url) {
        console.log('Document URL:', url);
        setCurrentDocument({
          url: url,
          type: document.key,
          name: document.name,
          originalUrl: vehicle?.documents?.[document.key]
        });
        setShowDocumentModal(true);
      } else {
        alert('Document not available or access denied');
      }
    } catch (error) {
      console.error('Error accessing document:', error);
      alert('Error accessing document');
    } finally {
      setIsLoadingDocument(false);
    }
  };

  const handleCloseDocumentModal = () => {
    setShowDocumentModal(false);
    setCurrentDocument(null);
  };

  const handleDownload = async (document) => {
    try {
      const url = await getDocumentUrl(document.key);
      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `${vehicle.regNo}_${document.name.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Document not available for download');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Error downloading document');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Vehicle Documents - {vehicle?.regNo}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc) => {
            const Icon = doc.icon;
            const hasDocument = vehicle?.documents?.[doc.key];
            
            return (
              <div
                key={doc.key}
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                  hasDocument 
                    ? 'border-gray-300 hover:border-primary-500 hover:bg-primary-50' 
                    : 'border-gray-200 bg-gray-50 opacity-50'
                }`}
                onClick={() => hasDocument && handleDocumentClick(doc)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Icon className="h-5 w-5 text-gray-600" />
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {doc.name}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {hasDocument ? 'Available' : 'Not uploaded'}
                      </p>
                    </div>
                  </div>
                  {hasDocument && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(doc);
                      }}
                      className="text-primary-600 hover:text-primary-800"
                      title="Download"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

      </div>

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        isOpen={showDocumentModal}
        onClose={handleCloseDocumentModal}
        documentUrl={currentDocument?.url}
        documentType={currentDocument?.type}
        documentName={currentDocument?.name}
        originalUrl={currentDocument?.originalUrl}
      />
    </div>
  );
};

export default VehicleDocumentViewer; 